import React, { useState, KeyboardEvent } from 'react';
import { XIcon, TagIcon } from 'lucide-react';

interface TagsInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  error?: string;
}

export function TagsInput({ value, onChange, error }: TagsInputProps) {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      const newTag = inputValue.trim().toLowerCase();

      // Don't add duplicate tags
      if (!value.includes(newTag)) {
        onChange([...value, newTag]);
      }
      setInputValue('');
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag on backspace if input is empty
      onChange(value.slice(0, -1));
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((tag) => tag !== tagToRemove));
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Tags
        <span className="text-gray-400 text-xs ml-1">(press Enter to add)</span>
      </label>

      <div
        className={`w-full px-3 py-2 rounded-xl border ${
          error ? 'border-red-500' : 'border-gray-300'
        } focus-within:border-[#5B8E87] focus-within:ring-2 focus-within:ring-[#5B8E87] focus-within:ring-opacity-20 transition-all bg-white`}
      >
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-3 py-1 bg-[#5B8E87] bg-opacity-10 text-[#5B8E87] text-sm rounded-full font-medium"
            >
              <TagIcon className="w-3 h-3" />
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="hover:bg-[#5B8E87] hover:bg-opacity-20 rounded-full p-0.5 transition-colors"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>

        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? 'Add tags...' : 'Add more tags...'}
          className="w-full outline-none text-sm"
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <p className="text-xs text-gray-500">
        Tags help you organize and find templates quickly
      </p>
    </div>
  );
}
